import { useState, useEffect } from 'react';
import { useClasses, useSubmit } from '../hooks';
import { PageHeader, Select } from '../components/UI';
import { CheckSquare, X, Clock, MessageCircle, Save, Users } from 'lucide-react';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { format } from 'date-fns';

const STATUS_OPTS = [
  { value: 'present', label: '✅ Present', color: '#0f9d58', bg: '#e6f4ea' },
  { value: 'absent', label: '❌ Absent', color: '#d93025', bg: '#fce8e6' },
  { value: 'late', label: '⏰ Late', color: '#f29900', bg: '#fef3e2' },
  { value: 'half_day', label: '½ Half Day', color: '#7c4dff', bg: '#ede7f6' },
];

export default function Attendance() {
  const [classId, setClassId] = useState('');
  const [date, setDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [students, setStudents] = useState([]);
  const [attendance, setAttendance] = useState({});
  const [loading, setLoading] = useState(false);
  const [sendWA, setSendWA] = useState(true);
  const [tab, setTab] = useState('mark'); // 'mark' | 'report'
  const [monthReport, setMonthReport] = useState(null);
  const [reportMonth, setReportMonth] = useState(format(new Date(), 'yyyy-MM'));

  const { classes } = useClasses();
  const { loading: saving, submit } = useSubmit();

  const classOpts = classes.map(c => ({ value: c.id, label: `${c.name} - ${c.section}` }));

  // Load students and existing attendance
  useEffect(() => {
    if (!classId) return;
    const load = async () => {
      setLoading(true);
      try {
        const [studRes, attRes] = await Promise.all([
          api.get(`/students?class_id=${classId}&limit=100`),
          api.get(`/attendance?class_id=${classId}&date=${date}`)
        ]);
        const studs = studRes.data.data || [];
        setStudents(studs);
        const existingAtt = {};
        attRes.data.forEach(a => { existingAtt[a.student_id] = a.status; });
        // Default all to present if not set
        const defaultAtt = {};
        studs.forEach(s => { defaultAtt[s.student_id] = existingAtt[s.student_id] || 'present'; });
        setAttendance(defaultAtt);
      } catch { toast.error('Failed to load'); }
      finally { setLoading(false); }
    };
    load();
  }, [classId, date]);

  const markAll = (status) => {
    const all = {};
    students.forEach(s => { all[s.student_id] = status; });
    setAttendance(all);
  };

  const toggleStatus = (studentId, status) => {
    setAttendance(a => ({ ...a, [studentId]: status }));
  };

  const handleSave = async () => {
    if (!classId || !students.length) return toast.error('Select class first');
    const attData = students.map(s => ({ student_id: s.student_id, status: attendance[s.student_id] || 'present' }));
    const result = await submit(() => api.post('/attendance/bulk', { class_id: classId, date, attendance_data: attData, send_whatsapp: sendWA }),
      `Attendance saved! ${sendWA ? 'WhatsApp alerts sent to absent students.' : ''}`);
  };

  const loadReport = async () => {
    if (!classId) return toast.error('Select a class');
    try {
      const { data } = await api.get(`/attendance/monthly-report?class_id=${classId}&month=${reportMonth}`);
      setMonthReport(data);
    } catch { toast.error('Failed to load report'); }
  };

  const downloadCSV = async () => {
    if (!classId) return toast.error('Select a class');
    const link = document.createElement('a');
    link.href = `${api.defaults.baseURL}/reports/attendance-csv?class_id=${classId}&month=${reportMonth}`;
    link.setAttribute('download', `attendance_${reportMonth}.csv`);
    link.click();
  };

  const summary = {
    present: Object.values(attendance).filter(v => v === 'present').length,
    absent: Object.values(attendance).filter(v => v === 'absent').length,
    late: Object.values(attendance).filter(v => v === 'late').length,
  };

  return (
    <div>
      <PageHeader title="Attendance" subtitle="Mark & manage student attendance" />

      {/* Tabs */}
      <div className="tabs">
        <button className={`tab-btn ${tab === 'mark' ? 'active' : ''}`} onClick={() => setTab('mark')}>Mark Attendance</button>
        <button className={`tab-btn ${tab === 'report' ? 'active' : ''}`} onClick={() => setTab('report')}>Monthly Report</button>
      </div>

      {tab === 'mark' && (
        <>
          {/* Filters */}
          <div className="card" style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div style={{ flex: 1, minWidth: 200 }}>
                <label className="form-label">Class *</label>
                <Select value={classId} onChange={setClassId} options={classOpts} placeholder="Select Class" />
              </div>
              <div>
                <label className="form-label">Date</label>
                <input className="form-control" type="date" value={date} onChange={e => setDate(e.target.value)} />
              </div>
            </div>
          </div>

          {classId && (
            <>
              {/* Summary + actions */}
              <div style={{ display: 'flex', gap: 14, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
                {/* Summary chips */}
                {[
                  { label: 'Present', val: summary.present, color: '#0f9d58', bg: '#e6f4ea' },
                  { label: 'Absent', val: summary.absent, color: '#d93025', bg: '#fce8e6' },
                  { label: 'Late', val: summary.late, color: '#f29900', bg: '#fef3e2' },
                ].map(s => (
                  <div key={s.label} style={{ padding: '6px 16px', borderRadius: 999, background: s.bg, color: s.color, fontWeight: 700, fontSize: '0.85rem' }}>
                    {s.label}: {s.val}
                  </div>
                ))}
                <div style={{ flex: 1 }} />
                {/* Mark all buttons */}
                <button className="btn btn-secondary btn-sm" onClick={() => markAll('present')}><CheckSquare size={14}/> All Present</button>
                <button className="btn btn-secondary btn-sm" onClick={() => markAll('absent')}><X size={14}/> All Absent</button>
                {/* WhatsApp toggle */}
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600 }}>
                  <input type="checkbox" checked={sendWA} onChange={e => setSendWA(e.target.checked)} />
                  <MessageCircle size={14} color="#25d366" /> Send WhatsApp to Absent Parents
                </label>
                <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                  {saving ? <><span className="spinner" /> Saving...</> : <><Save size={15} /> Save Attendance</>}
                </button>
              </div>

              {/* Students grid */}
              {loading
                ? <div className="loading-screen"><div className="spinner spinner-dark"/><span>Loading students...</span></div>
                : (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 12 }}>
                    {students.map(s => (
                      <div key={s.student_id} style={{ background: '#fff', borderRadius: 12, border: '1.5px solid var(--border)', padding: '14px 16px', display: 'flex', gap: 12, alignItems: 'center' }}>
                        <div style={{ width: 38, height: 38, borderRadius: '50%', background: 'var(--primary-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '0.85rem', color: 'var(--primary)', flexShrink: 0, overflow: 'hidden' }}>
                          {s.photo_url ? <img src={s.photo_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : s.full_name?.split(' ').map(w => w[0]).join('').slice(0,2)}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 700, fontSize: '0.875rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.full_name}</div>
                          <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>Roll: {s.roll_no} | {s.admission_no}</div>
                        </div>
                        <div style={{ display: 'flex', gap: 4 }}>
                          {STATUS_OPTS.map(opt => (
                            <button key={opt.value} title={opt.label}
                              onClick={() => toggleStatus(s.student_id, opt.value)}
                              style={{
                                width: 28, height: 28, borderRadius: 6, border: '1.5px solid',
                                borderColor: attendance[s.student_id] === opt.value ? opt.color : '#e0e7ff',
                                background: attendance[s.student_id] === opt.value ? opt.bg : 'transparent',
                                cursor: 'pointer', fontSize: '0.7rem', fontWeight: 700, color: opt.color,
                                transition: 'all 0.15s'
                              }}>
                              {opt.value === 'present' ? 'P' : opt.value === 'absent' ? 'A' : opt.value === 'late' ? 'L' : 'H'}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )
              }
            </>
          )}
        </>
      )}

      {tab === 'report' && (
        <>
          <div className="card" style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div style={{ flex: 1, minWidth: 200 }}>
                <label className="form-label">Class *</label>
                <Select value={classId} onChange={setClassId} options={classOpts} placeholder="Select Class" />
              </div>
              <div>
                <label className="form-label">Month</label>
                <input className="form-control" type="month" value={reportMonth} onChange={e => setReportMonth(e.target.value)} />
              </div>
              <button className="btn btn-primary" onClick={loadReport}><Users size={15}/> Load Report</button>
              <button className="btn btn-secondary" onClick={downloadCSV}><CheckSquare size={15}/> Download CSV</button>
            </div>
          </div>

          {monthReport && (
            <div className="card">
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Roll</th>
                      <th>Student</th>
                      {monthReport.dates.map(d => <th key={d} style={{ fontSize: '0.7rem', padding: '8px 6px' }}>{format(new Date(d), 'dd')}</th>)}
                      <th>Present</th>
                      <th>%</th>
                    </tr>
                  </thead>
                  <tbody>
                    {monthReport.students.map(s => {
                      const sAtt = monthReport.attendanceMap[s.id] || {};
                      const presentCount = Object.values(sAtt).filter(v => v === 'present').length;
                      const pct = monthReport.dates.length ? Math.round(presentCount / monthReport.dates.length * 100) : 0;
                      return (
                        <tr key={s.id}>
                          <td style={{ fontWeight: 700 }}>{s.roll_no}</td>
                          <td style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{s.full_name}</td>
                          {monthReport.dates.map(d => {
                            const status = sAtt[d];
                            return (
                              <td key={d} style={{ padding: '6px', textAlign: 'center' }}>
                                <div style={{
                                  width: 22, height: 22, borderRadius: 4, margin: '0 auto',
                                  background: status === 'present' ? '#0f9d58' : status === 'absent' ? '#d93025' : status === 'late' ? '#f29900' : '#e0e7ff',
                                  color: status ? '#fff' : 'transparent', fontSize: '0.6rem', fontWeight: 700,
                                  display: 'flex', alignItems: 'center', justifyContent: 'center'
                                }}>
                                  {status ? status[0].toUpperCase() : ''}
                                </div>
                              </td>
                            );
                          })}
                          <td style={{ fontWeight: 700 }}>{presentCount}/{monthReport.dates.length}</td>
                          <td>
                            <span style={{ fontWeight: 700, color: pct >= 75 ? '#0f9d58' : '#d93025' }}>{pct}%</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
